import React, { useEffect, useState } from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import {
  Paper,
  Box,
  useTheme,
  IconButton,
  Typography,
  Tooltip,
} from "@mui/material";
import {
  AssignmentReturn,
  Badge,
  ExpandLess,
  ExpandMore,
} from "@mui/icons-material";
import moment from "moment";
import participantSchema from "./participantSchema";
import PATCloseButton from "../../../components/Buttons/PATCloseButton";
import PATSaveButton from "../../../components/Buttons/PATSaveButton";
import FormSection from "../../../components/Form/FormSection";
import { useParticipantData } from "./useParticipantData";
import {
  createTrainingParticipant,
  updateTrainingParticipant,
} from "../../../reducers/TrainingParticipantReducer";
import { getParticipantFields } from "./participantFieldConfigs";
import ParticipantCostTable from "./ParticipantCostTable";
import {
  createTrainingCost,
  getTrainingCostsByParticipant,
} from "../../../reducers/TrainingCostReducer";
import {
  PATH_TRAINING_PARTICIPANTS,
  PATH_TRAINING_COURSES_EDIT,
  PATH_PARTICIPANTS_EDIT,
} from "../../../utils/routeConstants";
import TagsTable from "../../../components/Tags/TagsTable";
import { PerTagType } from "../../../utils/enums";
import { getEmployeeOptionLabel, printAMSReport } from "../../../utils/functions";
import { areAllPermissionsDisabled, formatModulePermission, getAccessPermission, getModuleAccessPermission } from "../../../reducers/PermissionsReducer";
import { notify } from "../../../reducers/NotifierReducer";
import { AMSReportID, entityNamesForCustomId, serviceNames } from "../../../utils/constants";
import { getService } from "../../../reducers/service";
import PATPrintButton from "../../../components/Buttons/PATPrintButton";
import PrintBase64Pdf from "../../../components/PrintBase64Pdf";

const AddEditTrainingParticipant = () => {
  const { id, courseId } = useParams();
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();
  const [selectedCourse, setSelectedCourse] = useState(null);
  const queryParams = new URLSearchParams(location.search);
  const readOnly = queryParams.get("readOnly") === "true";

  useEffect(() => {
    if (location.search && queryParams.get("readOnly") !== "true") {
      dispatch(notify("The requested page does not exist.", "warning"));
      history.push(PATH_TRAINING_PARTICIPANTS);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, location.search, history]);

  const fromCourse = location.state?.fromCourse;

  const [allExpanded, setAllExpanded] = useState(true);
  const [printReport, setPrintReport] = useState(false);
  const [reportString, setReportString] = useState("");

  const handleToggleAll = () => {
    setAllExpanded((prev) => !prev);
  };

  const {
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(participantSchema),
    defaultValues: {
      id: null,
      employeeNum: "",
      firstName: "",
      lastName: "",
      course: "",
      courseNumber: "",
      courseDate: "",
      attendedDate: "",
      courseLocation: "",
      dateRegistered: "",
      hours: "",
      grade: "",
      approved: false,
      checkSent: false,
      dateCheckSent: "",
      category: "",
      approvedBy: "",
      comments: "",
      participantNumber: "",
    },
  });

  const {
    loaded,
    locationOptions,
    categoryOptions,
    employees,
    courses,
    tagTypeOptions,
  } = useParticipantData(id, reset, courseId);

  const options = {
    courseLocation: locationOptions,
    category: categoryOptions,
    employeeNum: employees.map((emp) => ({
      label: getEmployeeOptionLabel(emp),
      value: emp.employeeNum,
    })),
    courseNumber: courses.map((course) => ({
      label: `${course.courseNumber} - ${course.courseName}`,
      value: course.courseNumber,
    })),
  };

  const { costsByParticipant } = useSelector((state) => state.trainingCosts);
  const user = useSelector((state) => state.user);

  const personnelTags = useSelector((state) =>
    state.personnelTags.personnelTags &&
    state.personnelTags.personnelTags[PerTagType.Participant]
      ? state.personnelTags.personnelTags[PerTagType.Participant][id]
      : []
  );

  const trainingPermission = getModuleAccessPermission("training", "any");
  const trainingModulePermissions = formatModulePermission(
    "TrainingModule",
    trainingPermission,
    [
      "Courses",
      "Participants",
      "Certifications",
      "Summary",
      "Mass Update",
      "Course Information",
      "Course Participant",
      "Course Certificate Information",
      "Course Tags",
      "Certificate Information",
      "Certificate Tags"
    ]
  );
  const isDisabledModule = areAllPermissionsDisabled(
    trainingModulePermissions["TrainingModule"]
  );

  const participantInfoTabPermission = getAccessPermission(
    "training",
    "Participant Information",
    "any"
  );
  const participantInfoTabReadPermission =
    participantInfoTabPermission?.Read ?? true;
  const participantInfoTabEditPermission =
    participantInfoTabPermission?.Edit ?? true;

  const participantTagsTabPermission = getAccessPermission(
    "training",
    "Participant Tags",
    "any"
  );
  const participantTagsTabReadPermission =
    participantTagsTabPermission?.Read ?? true;

  const trainingCostTabPermission = getAccessPermission(
    "training",
    "Training Cost",
    "any"
  );
  const trainingCostTabReadPermission = trainingCostTabPermission?.Read ?? true;
  const trainingCostTabAddPermission =
    trainingCostTabPermission?.Create ?? true;
  const trainingCostTabEditPermission = trainingCostTabPermission?.Edit ?? true;
  const trainingCostTabDeletePermission =
    trainingCostTabPermission?.Delete ?? true;

  const participantCosts = costsByParticipant ? costsByParticipant[id] : [];

  useEffect(() => {
    if (id) {
      dispatch(getTrainingCostsByParticipant(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    if (!id && courseId && courses.length) {
      const selected = courses.find(
        (course) => String(course.id) === String(courseId)
      );

      if (selected) {
        setSelectedCourse(selected);
        setValue("course", selected.courseName);
        setValue(
          "courseDate",
          selected.courseEndDate
            ? moment(selected.courseEndDate).format("MM/DD/YYYY")
            : ""
        );
        setValue(
          "attendedDate",
          selected.courseBeginDate
            ? moment(selected.courseBeginDate).format("MM/DD/YYYY")
            : ""
        );
        setValue("hours", selected.hours);
        setValue("courseLocation", selected.location);
        setValue("category", selected.category);
        setValue("courseNumber", selected.courseNumber);
      }
    }
  }, [id, courses, courseId, setValue]);

  useEffect(() => {
    setValue("participantNumber", "");
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user.userAgency]);

  const selectedEmployeeId = watch("employeeNum");
  const selectedCourseNumber = watch("courseNumber");

  useEffect(() => {
    if (!selectedEmployeeId || !employees.length) {
      setValue("firstName", "");
      setValue("lastName", "");
      return;
    }

    const selected = employees.find(
      (emp) => String(emp.employeeNum) === String(selectedEmployeeId)
    );

    if (selected) {
      setValue("firstName", selected.firstName);
      setValue("lastName", selected.lastName);
    }
  }, [selectedEmployeeId, employees, setValue]);

  useEffect(() => {
    if (!selectedCourseNumber || !courses.length) {
      setValue("course", "");
      setValue("courseDate", "");
      setValue("attendedDate", "");
      setValue("hours", "");
      setValue("courseLocation", "");
      setValue("category", "");
      return;
    }

    const selected = courses.find(
      (course) => String(course.courseNumber) === String(selectedCourseNumber)
    );

    if (selected) {
      setSelectedCourse(selected);
      setValue("course", selected.courseName);
      setValue(
        "courseDate",
        selected.courseEndDate
          ? moment(selected.courseEndDate).format("MM/DD/YYYY")
          : ""
      );
      setValue(
        "attendedDate",
        selected.courseBeginDate
          ? moment(selected.courseBeginDate).format("MM/DD/YYYY")
          : ""
      );
      setValue("hours", selected.hours);
      setValue("courseLocation", selected.location);
      setValue("category", selected.category);
    }
  }, [selectedCourseNumber, courses, setValue]);

  const generateCustomId = async () => {
    const data = {
      EntityName: entityNamesForCustomId.ParticipantId,
      agencyID: user.userAgency,
    };
    const service = getService(serviceNames.customId);
    service.timeout = 20000;

    const id = await service.create(data);
    if (id?.includes("Agency ID does not match")) {
      dispatch(
        notify(
          "Unable to generate Participant ID because the Agency ID does not match.",
          "warning"
        )
      );
      return null;
    }
    return id;
  };

  const handleAutoGenerateClick = async () => {
    const id = await generateCustomId();
    setValue("participantNumber", id);
  };

  const onSubmit = async (data) => {
    const participantId = await generateCustomId();
    const cleanedData = {
      employeeNum: data.employeeNum || null,
      lastName: data.lastName.trim() || null,
      firstName: data.firstName.trim() || null,
      course: data.course || null,
      courseNumber: data.courseNumber || null,
      courseDate: data.courseDate
        ? moment(data.courseDate, "MM/DD/YYYY").format("YYYY-MM-DD")
        : null,
      attendedDate: data.attendedDate
        ? moment(data.attendedDate, "MM/DD/YYYY").format("YYYY-MM-DD")
        : null,
      courseLocation: data.courseLocation || null,
      dateRegistered: data.dateRegistered
        ? moment(data.dateRegistered, "MM/DD/YYYY").format("YYYY-MM-DD")
        : null,
      hours:
        data.hours !== "" && !isNaN(Number(data.hours))
          ? Number(data.hours)
          : null,
      grade: data.grade.trim() || null,
      approved: data.approved ?? null,
      checkSent: data.checkSent ?? null,
      dateCheckSent: data.dateCheckSent
        ? moment(data.dateCheckSent, "MM/DD/YYYY").format("YYYY-MM-DD")
        : null,
      category: data.category.trim() || null,
      approvedBy: data.approvedBy.trim() || null,
      perTrainingCourseId: courseId || selectedCourse?.id,
      agencyId: user.userAgency,
      participantNumber: data.participantNumber || participantId,
      comments: data?.comments || "",
    };

    if (id) {
      await dispatch(updateTrainingParticipant(data.id, cleanedData));
      history.push(`${PATH_TRAINING_PARTICIPANTS}`);
    } else {
      await dispatch(createTrainingParticipant(cleanedData)).then(
        async (created) => {
          if (created.perTrainingCourseId && created.id) {
            if (selectedCourse.tuitionDefault) {
              const payload = {
                title: "TUITION",
                cost: selectedCourse.tuitionDefault,
                perTrainingId: created.id,
              };
              await dispatch(createTrainingCost(payload));
            }
            history.push(
              `${PATH_TRAINING_COURSES_EDIT}${created.perTrainingCourseId}${PATH_PARTICIPANTS_EDIT}${created.id}`
            );
          } else {
            history.push(`${PATH_TRAINING_PARTICIPANTS}`);
          }
        }
      );
    }
  };

  if (!loaded) return null;

  const sectionProps = {
    control,
    errors,
    options,
    theme,
    watch,
  };

  const renderIdGenerationButton = () => {
    return (
      <Box>
        <Tooltip title="Auto generate participant number">
          <IconButton
            color="primary"
            aria-label="Auto generate participant number"
            size="large"
            onClick={handleAutoGenerateClick}
          >
            <AssignmentReturn />
          </IconButton>
        </Tooltip>
      </Box>
    );
  };

  const formSections = [
    participantInfoTabReadPermission && {
      title: "Participant Information",
      icon: <Badge />,
      fields: getParticipantFields(courseId),
      allExpanded,
      defaultExpanded: true,
      editPermission: id ? !readOnly && participantInfoTabEditPermission : true,
      isEdit: id ? true : false,
      renderIdGenerationButton: renderIdGenerationButton,
    },
  ];

  const handleClose = () => {
    if (fromCourse) {
      history.push(`${PATH_TRAINING_COURSES_EDIT}${courseId}`);
    } else {
      history.push(PATH_TRAINING_PARTICIPANTS);
    }
  };

  const handlePrintParticipantProfile = async () => {
    const result = await printAMSReport(
      AMSReportID.TrainingRecords,
      id,
      user.userAgency,
      trainingModulePermissions,
      user.userData.user.Username
    );

    if (result) {
      setReportString(result?.base64 || "");
      setPrintReport(true);
    }
  };

  return (
    loaded && (
      <Paper
        elevation={3}
        sx={{
          padding: theme.spacing(3),
          margin: theme.spacing(1),
          backgroundColor: theme.palette.background.paper,
          overflow: "hidden",
          height: "calc(100vh - 100px)",
          marginTop: theme.spacing(0.5),
        }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <IconButton
              onClick={handleToggleAll}
              title={allExpanded ? "Collapse All" : "Expand All"}
            >
              {allExpanded ? <ExpandLess /> : <ExpandMore />}
            </IconButton>
            <Typography variant="h4" gutterBottom>
              {id ? "Edit Participant" : "Add Participant"}
            </Typography>
          </Box>
          <Box display="flex" gap={2}>
            {!isDisabledModule && (
              <>
                {id && (
                  <PATPrintButton
                    tooltipText="Print Participant Profile"
                    color="primary"
                    onClick={handlePrintParticipantProfile}
                  />
                )}
                {!readOnly && (
                  <PATSaveButton
                    variant="contained"
                    color="primary"
                    onClick={handleSubmit(onSubmit)}
                  />
                )}
              </>
            )}
            <PATCloseButton tooltipText="Close" onClick={() => handleClose()} />
          </Box>
        </Box>

        <Box sx={{ height: "70vh", overflowY: "auto", pr: 1 }}>
          <form>
            {formSections.filter(Boolean).map((section) => (
              <FormSection key={section.title} {...section} {...sectionProps} />
            ))}
          </form>
          {id ? (
            <Box sx={{ mt: 1 }}>
              <>
                {trainingCostTabReadPermission && (
                  <ParticipantCostTable
                    id={id}
                    theme={theme}
                    participantId={id}
                    participantCosts={participantCosts}
                    allExpanded={allExpanded}
                    canAdd={!readOnly && trainingCostTabAddPermission}
                    canEdit={!readOnly && trainingCostTabEditPermission}
                    canDelete={!readOnly && trainingCostTabDeletePermission}
                  />
                )}

                {participantTagsTabReadPermission && (
                  <TagsTable
                    id={id}
                    tagTypeOptions={tagTypeOptions}
                    personnelTags={personnelTags}
                    theme={theme}
                    allExpanded={allExpanded}
                    perTagType={PerTagType.Participant}
                    readOnly={readOnly}
                  />
                )}
              </>
            </Box>
          ) : (
            <></>
          )}
        </Box>
        <PrintBase64Pdf base64={reportString} open={printReport} />
      </Paper>
    )
  );
};

export default AddEditTrainingParticipant;
